import { isNil } from "lodash";

import { useTranslate } from "@similarweb/i18n-provider";
import { colorsPalettes } from "@similarweb/styles";
import { Button, IconButton, Tooltip } from "@similarweb/ui-components";

import { projectsDataAutomation } from "app/pages/activation/components/HomeWithData/dataAutomationKeys";
import { Items } from "app/pages/activation/components/HomeWithData/Projects/Items";
import { ProjectsListModal } from "app/pages/activation/components/HomeWithData/Projects/ProjectsListModal/ProjectsListModal";
import {
    CREATE_PROJECT,
    PROJECT_TITLE,
    VIEW_ALL_BUTTON,
} from "app/pages/activation/components/HomeWithData/Projects/texts";
import { SectionTitle } from "app/pages/activation/components/HomeWithData/SectionTitle";

import * as SC from "./styles";
import { useProjects } from "./useProjects";

type ProjectsSchema = {
    onClickCreateProject: () => void;
};

export const Projects = ({ onClickCreateProject }: ProjectsSchema) => {
    const { translate } = useTranslate();

    const {
        images,
        isImagesLoading,
        activations,
        totalNumber,
        isMetadataLoading,
        isProjectsListModalOpen,
        isActivationLimitReached,
        onCloseProjectsListModal,
        maxProjects,
        canBuyProjects,
        onCreateProjectClick,
        onAddonOpen,
        onViewAllClick,
        onActivationItemClick,
    } = useProjects(onClickCreateProject);

    return (
        <>
            <ProjectsListModal
                isOpen={isProjectsListModalOpen}
                onClose={onCloseProjectsListModal}
                onClickCreateProject={onCreateProjectClick("cta")}
            />

            <SC.Container>
                <SC.TopContainer>
                    <SectionTitle
                        title={translate(PROJECT_TITLE)}
                        dataAutomation={projectsDataAutomation.container}
                    />

                    {!isMetadataLoading && !isNil(totalNumber) ? (
                        <Button
                            type="flat"
                            dataAutomation={projectsDataAutomation.seeAllButton}
                            onClick={onViewAllClick}
                            isDisabled={!totalNumber}
                        >
                            {translate(VIEW_ALL_BUTTON, { exist: totalNumber, available: maxProjects })}
                        </Button>
                    ) : null}

                    <SC.CreateBtnContainer data-automation={projectsDataAutomation.createButton}>
                        <Tooltip content={translate(CREATE_PROJECT)}>
                            <IconButton
                                iconSize="xs"
                                iconName="add"
                                iconColor={colorsPalettes.carbon[0]}
                                onClick={
                                    canBuyProjects && isActivationLimitReached
                                        ? onAddonOpen
                                        : onCreateProjectClick("plus icon")
                                }
                            />
                        </Tooltip>
                    </SC.CreateBtnContainer>
                </SC.TopContainer>

                <SC.BottomContainer data-automation={projectsDataAutomation.projectsContainer}>
                    <Items
                        onActivationClick={onActivationItemClick}
                        onCreate={onCreateProjectClick}
                        activations={activations}
                        isMetadataLoading={isMetadataLoading}
                        images={images}
                        isImagesLoading={isImagesLoading}
                    />
                </SC.BottomContainer>
            </SC.Container>
        </>
    );
};
